/**
 * Gum Upsell (B) — Best-Sellers 4-Pack
 *
 * Public API:
 *   el.hasCommittedSelection()
 *   el.getCommittedSelection()
 *   el.getCommittedTotalsCents()
 *   el.buildCartItems()
 *
 * Events:
 *   gum-upsell-b:committed
 *   gum-upsell-b:removed
 *   gum-upsell-b:totals-changed
 */
(() => {
  const MODAL_OPEN_CLASS = 'is-open';
  const BODY_LOCK_CLASS = 'gum-upsell-b-modal-open';
  const SCROLL_UNLOCK_MS = 380;

  const toInt = (value, fallback = 0) => {
    const n = parseInt(value, 10);
    return Number.isFinite(n) ? n : fallback;
  };

  /**
   * Shopify money formats (incl. Markets / non-USD) e.g.
   * "{{amount_no_decimals_with_space_separator}} som"
   */
  const formatMoney = (cents, moneyFormat = '${{amount}}') => {
    const formatString = String(moneyFormat || '${{amount}}');
    const rawCents = Number(cents);
    const safeCents = Number.isFinite(rawCents) ? rawCents : 0;

    const formatWithDelimiters = (numberCents, precision = 2, thousands = ',', decimal = '.') => {
      const value = (numberCents / 100).toFixed(precision);
      const parts = value.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      const centsPart = parts[1] ? `${decimal}${parts[1]}` : '';
      return dollars + centsPart;
    };

    const placeholderMatch = formatString.match(/\{\{\s*(\w+)\s*\}\}/);
    const placeholder = placeholderMatch?.[1] || 'amount';
    let value = '';

    switch (placeholder) {
      case 'amount_no_decimals':
        value = formatWithDelimiters(safeCents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(safeCents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(safeCents, 0, '.', ',');
        break;
      case 'amount_with_space_separator':
        value = formatWithDelimiters(safeCents, 2, ' ');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = formatWithDelimiters(safeCents, 0, ' ');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(safeCents, 2, "'");
        break;
      case 'amount_no_decimals_with_apostrophe_separator':
        value = formatWithDelimiters(safeCents, 0, "'");
        break;
      case 'amount':
      default:
        value = formatWithDelimiters(safeCents, 2);
        break;
    }

    return formatString.replace(/\{\{\s*\w+\s*\}\}/, value);
  };

  const measureScrollbarGap = () =>
    Math.max(0, window.innerWidth - document.documentElement.clientWidth);

  class GumUpsellB extends HTMLElement {
    constructor() {
      super();
      this.onClick = this.onClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.committed = false;
      this.purchaseType = 'subscription';
      this.packItems = [];
      this.lastFocus = null;
      this.scrollUnlockTimer = null;
    }

    connectedCallback() {
      if (this._connected) return;
      this._connected = true;

      this.cacheElements();
      this.readConfig();
      this.purchaseType = this.defaultPurchaseType;
      this.packItems = this.readPackItems();
      this.addEventListener('click', this.onClick);
      document.addEventListener('keydown', this.onKeydown);
      this.renderCommittedUi();
      this.syncModalUi();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      document.removeEventListener('keydown', this.onKeydown);
      this.releaseScrollLock();
      this._connected = false;
    }

    cacheElements() {
      this.cardEl = this.querySelector('[data-gum-upsell-b-card]');
      this.priceDefaultEl = this.querySelector('[data-gum-upsell-b-price-default]');
      this.priceCommittedEl = this.querySelector('[data-gum-upsell-b-price-committed]');
      this.compareEl = this.querySelector('[data-gum-upsell-b-compare]');
      this.saleEl = this.querySelector('[data-gum-upsell-b-sale]');
      this.badgeEl = this.querySelector('[data-gum-upsell-b-badge]');
      this.planEl = this.querySelector('[data-gum-upsell-b-plan]');
      this.unitCommittedEl = this.querySelector('[data-gum-upsell-b-unit-committed]');
      this.ctaBtn = this.querySelector('[data-gum-upsell-b-cta]');
      this.ctaLabelEl = this.querySelector('[data-gum-upsell-b-cta-label]');
      this.modal = this.querySelector('[data-gum-upsell-b-modal]');
      this.dialog = this.modal?.querySelector('.gum-upsell-b-modal__dialog') || null;
      this.footerCompareEl = this.querySelector('[data-gum-upsell-b-footer-compare]');
      this.footerTotalEl = this.querySelector('[data-gum-upsell-b-footer-total]');
      this.footerBadgeEl = this.querySelector('[data-gum-upsell-b-footer-badge]');
      this.footerMetaEl = this.querySelector('[data-gum-upsell-b-footer-meta]');
      this.purchaseToggleEl = this.querySelector('[data-gum-upsell-b-purchase-toggle]');
    }

    readConfig() {
      this.moneyFormat = this.getAttribute('data-money-format') || '${{amount}}';
      this.unitPriceCents = toInt(this.getAttribute('data-unit-price-cents'), 0);
      this.unitSaleCents = toInt(this.getAttribute('data-unit-sale-cents'), 0);
      this.packSaleCents = toInt(this.getAttribute('data-pack-sale-cents'), 0);
      this.packCompareCents = toInt(this.getAttribute('data-pack-compare-cents'), 0);
      this.packQuantity = toInt(this.getAttribute('data-pack-quantity'), 4);
      this.defaultPurchaseType = this.getAttribute('data-default-purchase-type') || 'subscription';
      this.subscriptionLabel = this.getAttribute('data-subscription-label') || 'Subscription';
      this.onetimeLabel = this.getAttribute('data-onetime-label') || 'One-time';
      this.packSuffix = this.getAttribute('data-pack-suffix') || ' / pack';
      this.boxesLabel = this.getAttribute('data-boxes-label') || 'Boxes';
    }

    isSubscription() {
      return this.purchaseType === 'subscription';
    }

    readPackItems() {
      const script = this.querySelector('[data-gum-upsell-b-items]');
      if (!script) return [];
      try {
        const parsed = JSON.parse(script.textContent || '[]');
        if (!Array.isArray(parsed)) return [];
        return parsed
          .map((item) => ({
            id: item.id,
            title: String(item.title || ''),
            qty: Math.max(1, toInt(item.qty, 1)),
            onetimeCents: toInt(item.onetimeCents, 0),
            saleCents: toInt(item.saleCents, 0),
            sellingPlanId: item.sellingPlanId || null,
          }))
          .filter((item) => item.id);
      } catch (e) {
        console.warn('[GumUpsellB] Failed to parse pack items JSON', e);
        return [];
      }
    }

    showToast(message) {
      const text = String(message || '').trim();
      if (!text) return;

      let toast = this._toastEl || document.querySelector('[data-gum-upsell-b-toast]');
      if (!toast) {
        toast = document.createElement('div');
        toast.className = 'gum-upsell-b-toast';
        toast.setAttribute('data-gum-upsell-b-toast', '');
        toast.setAttribute('role', 'status');
        toast.setAttribute('aria-live', 'polite');
        toast.innerHTML =
          '<span class="gum-upsell-b-toast__icon" aria-hidden="true">' +
          '<svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">' +
          '<circle cx="9" cy="9" r="9" fill="#111111"/>' +
          '<path d="M5.2 9.1L7.8 11.6L12.8 6.4" stroke="white" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>' +
          '</svg></span>' +
          '<span class="gum-upsell-b-toast__text" data-gum-upsell-b-toast-text></span>';
        document.body.appendChild(toast);
      }
      this._toastEl = toast;

      const textEl = toast.querySelector('[data-gum-upsell-b-toast-text]');
      if (textEl) textEl.textContent = text;

      toast.classList.remove('is-visible');
      void toast.offsetWidth;
      toast.classList.add('is-visible');
      if (this._toastTimer) clearTimeout(this._toastTimer);
      this._toastTimer = setTimeout(() => {
        toast.classList.remove('is-visible');
        this._toastTimer = null;
      }, 2800);
    }

    applyPurchaseTypeUi() {
      this.dialog?.setAttribute('data-purchase-type', this.purchaseType);
      this.querySelectorAll('[data-gum-upsell-b-purchase]').forEach((btn) => {
        const active = btn.getAttribute('data-gum-upsell-b-purchase') === this.purchaseType;
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    }

    syncModalUi() {
      this.applyPurchaseTypeUi();

      const isSub = this.isSubscription();
      const totalCents = isSub ? this.packSaleCents : this.packCompareCents;
      const unitCents = isSub ? this.unitSaleCents : this.unitPriceCents;

      if (this.footerTotalEl) {
        this.footerTotalEl.textContent = formatMoney(totalCents, this.moneyFormat);
      }

      if (this.footerCompareEl) {
        if (isSub && this.packCompareCents > this.packSaleCents) {
          this.footerCompareEl.textContent = formatMoney(this.packCompareCents, this.moneyFormat);
          this.footerCompareEl.classList.remove('hidden');
        } else {
          this.footerCompareEl.classList.add('hidden');
        }
      }

      if (this.footerBadgeEl) {
        this.footerBadgeEl.classList.toggle('hidden', !isSub);
      }

      if (this.footerMetaEl) {
        this.footerMetaEl.textContent = `${formatMoney(unitCents, this.moneyFormat)}${this.packSuffix} · ${this.packQuantity} ${this.boxesLabel}`;
      }
    }

    renderCommittedUi() {
      const committed = this.committed;
      const isSub = this.isSubscription();

      if (this.priceDefaultEl) {
        this.priceDefaultEl.classList.toggle('hidden', committed);
        this.priceDefaultEl.hidden = committed;
      }
      if (this.priceCommittedEl) {
        this.priceCommittedEl.classList.toggle('hidden', !committed);
        this.priceCommittedEl.hidden = !committed;
      }

      if (committed) {
        const totalCents = isSub ? this.packSaleCents : this.packCompareCents;
        const unitCents = isSub ? this.unitSaleCents : this.unitPriceCents;

        if (this.saleEl) this.saleEl.textContent = formatMoney(totalCents, this.moneyFormat);

        if (this.compareEl) {
          if (isSub && this.packCompareCents > this.packSaleCents) {
            this.compareEl.textContent = formatMoney(this.packCompareCents, this.moneyFormat);
            this.compareEl.classList.remove('hidden');
          } else {
            this.compareEl.classList.add('hidden');
          }
        }

        if (this.badgeEl) {
          this.badgeEl.classList.toggle('hidden', !isSub);
        }

        if (this.planEl) {
          this.planEl.textContent = isSub ? this.subscriptionLabel : this.onetimeLabel;
        }

        if (this.unitCommittedEl) {
          this.unitCommittedEl.textContent = `${formatMoney(unitCents, this.moneyFormat)}${this.packSuffix}`;
        }
      }

      if (this.ctaBtn) {
        this.ctaBtn.classList.toggle('is-committed', committed);
        const addLabel = this.ctaBtn.getAttribute('data-add-label') || 'Add To My Order';
        const removeLabel = this.ctaBtn.getAttribute('data-remove-label') || 'Remove From My Order';
        if (this.ctaLabelEl) this.ctaLabelEl.textContent = committed ? removeLabel : addLabel;
      }

      this.cardEl?.classList.toggle('is-committed', committed);
      this.cardEl?.setAttribute('data-purchase-type', this.purchaseType);
    }

    applyScrollLock() {
      if (this.scrollUnlockTimer != null) {
        clearTimeout(this.scrollUnlockTimer);
        this.scrollUnlockTimer = null;
      }
      document.documentElement.style.setProperty(
        '--gum-upsell-b-scrollbar-gap',
        `${measureScrollbarGap()}px`
      );
      document.body.classList.add(BODY_LOCK_CLASS);
    }

    releaseScrollLock() {
      document.body.classList.remove(BODY_LOCK_CLASS);
      document.documentElement.style.removeProperty('--gum-upsell-b-scrollbar-gap');
    }

    scheduleScrollUnlock() {
      if (this.scrollUnlockTimer != null) {
        clearTimeout(this.scrollUnlockTimer);
        this.scrollUnlockTimer = null;
      }

      const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
      if (reduced) {
        this.releaseScrollLock();
        return;
      }

      this.scrollUnlockTimer = setTimeout(() => {
        this.scrollUnlockTimer = null;
        this.releaseScrollLock();
      }, SCROLL_UNLOCK_MS);
    }

    open() {
      if (!this.modal) return;
      this.lastFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      this.modal.classList.add(MODAL_OPEN_CLASS);
      this.modal.setAttribute('aria-hidden', 'false');
      this.applyScrollLock();
      this.syncModalUi();
      this.modal.querySelector('button[data-gum-upsell-b-close]')?.focus();
    }

    close() {
      if (!this.modal) return;
      this.modal.classList.remove(MODAL_OPEN_CLASS);
      this.modal.setAttribute('aria-hidden', 'true');
      this.scheduleScrollUnlock();

      if (this.lastFocus && document.contains(this.lastFocus)) {
        this.lastFocus.focus();
      }
      this.lastFocus = null;
    }

    openLearnMore() {
      if (this.committed) {
        // Keep committed purchase type when reopening.
        this.syncModalUi();
      } else {
        this.purchaseType = this.defaultPurchaseType;
      }
      this.open();
    }

    switchPurchaseType(nextType) {
      if (!nextType || nextType === this.purchaseType) return;
      this.purchaseType = nextType;
      this.syncModalUi();
    }

    commitSelection() {
      if (!this.packItems.length) {
        console.warn('[GumUpsellB] No matched pack variants to commit');
        return false;
      }

      this.committed = true;
      this.renderCommittedUi();
      this.notifyTotalsChanged();
      this.showToast(this.getAttribute('data-toast-added') || 'Best-Sellers 4-Pack added to your order');

      this.dispatchEvent(
        new CustomEvent('gum-upsell-b:committed', {
          bubbles: true,
          detail: this.getCommittedSelection(),
        })
      );
      return true;
    }

    removeCommitted() {
      if (!this.committed) return;
      this.committed = false;
      this.purchaseType = this.defaultPurchaseType;
      this.renderCommittedUi();
      this.syncModalUi();
      this.notifyTotalsChanged();
      this.showToast(this.getAttribute('data-toast-removed') || 'Best-Sellers 4-Pack removed from your order');

      this.dispatchEvent(
        new CustomEvent('gum-upsell-b:removed', {
          bubbles: true,
          detail: { blockId: this.getAttribute('data-block-id') },
        })
      );
    }

    notifyTotalsChanged() {
      this.dispatchEvent(
        new CustomEvent('gum-upsell-b:totals-changed', {
          bubbles: true,
          detail: this.getCommittedTotalsCents(),
        })
      );
    }

    onClick(event) {
      const target = event.target;
      if (!(target instanceof Element)) return;

      if (target.closest('[data-gum-upsell-b-learn]')) {
        this.openLearnMore();
        return;
      }

      if (target.closest('[data-gum-upsell-b-close]')) {
        this.close();
        return;
      }

      const purchaseBtn = target.closest('[data-gum-upsell-b-purchase]');
      if (purchaseBtn) {
        this.switchPurchaseType(purchaseBtn.getAttribute('data-gum-upsell-b-purchase') || 'subscription');
        return;
      }

      if (target.closest('[data-gum-upsell-b-modal-submit]')) {
        this.commitSelection();
        this.close();
        return;
      }

      if (target.closest('[data-gum-upsell-b-cta]')) {
        if (this.ctaBtn?.disabled) return;
        if (this.committed) {
          this.removeCommitted();
        } else {
          this.purchaseType = this.defaultPurchaseType;
          this.commitSelection();
        }
      }
    }

    onKeydown(event) {
      if (event.key !== 'Escape') return;
      if (!this.modal?.classList.contains(MODAL_OPEN_CLASS)) return;
      this.close();
    }

    hasCommittedSelection() {
      return Boolean(this.committed && this.packItems.length);
    }

    hasSubscriptionCommitted() {
      return this.hasCommittedSelection() && this.isSubscription();
    }

    getSubscriptionDiscountCode() {
      return String(this.getAttribute('data-subscription-discount-code') || '').trim();
    }

    getCommittedTotalsCents() {
      if (!this.hasCommittedSelection()) {
        return { saleCents: 0, compareCents: 0 };
      }
      if (this.isSubscription()) {
        return {
          saleCents: this.packSaleCents,
          compareCents: this.packCompareCents,
        };
      }
      return {
        saleCents: this.packCompareCents,
        compareCents: this.packCompareCents,
      };
    }

    getCommittedSelection() {
      if (!this.hasCommittedSelection()) return null;
      const totals = this.getCommittedTotalsCents();
      return {
        purchaseType: this.purchaseType,
        lines: this.packItems.map((item) => ({ ...item })),
        qty: this.packItems.reduce((sum, item) => sum + item.qty, 0),
        saleCents: totals.saleCents,
        compareCents: totals.compareCents,
        saveCents: Math.max(0, totals.compareCents - totals.saleCents),
      };
    }

    buildCartItems() {
      if (!this.hasCommittedSelection()) return [];

      const lineProperties = {
        _sources: 'Toothpaste Upsell',
        _0013: 'variation B',
      };
      const isSub = this.isSubscription();

      return this.packItems.map((line) => {
        const item = {
          id: Number(line.id),
          quantity: line.qty,
          properties: { ...lineProperties },
        };
        const planId = toInt(line.sellingPlanId, 0);
        if (isSub && planId > 0) item.selling_plan = planId;
        return item;
      });
    }
  }

  const toCartJsEndpoint = (routePath) => {
    const path = String(routePath || '').split('?')[0];
    if (!path) return '/cart/add.js';
    return path.endsWith('.js') ? path : `${path.replace(/\/?$/, '')}.js`;
  };

  const getCartAddEndpoint = () => {
    const routeUrl = window.routes?.cart_add_url;
    if (typeof routeUrl === 'string' && routeUrl.length > 0) {
      return toCartJsEndpoint(routeUrl);
    }
    const shopifyRoot = window.Shopify?.routes?.root ?? '/';
    const normalizedRoot = shopifyRoot.endsWith('/') ? shopifyRoot : `${shopifyRoot}/`;
    return `${normalizedRoot}cart/add.js`;
  };

  const getDiscountEndpoint = (code) => {
    const shopifyRoot = window.Shopify?.routes?.root ?? '/';
    const normalizedRoot = shopifyRoot.endsWith('/') ? shopifyRoot : `${shopifyRoot}/`;
    return `${normalizedRoot}discount/${encodeURIComponent(code)}`;
  };

  const applyDiscountCode = async (code) => {
    const trimmed = String(code || '').trim();
    if (!trimmed) return;
    try {
      await fetch(getDiscountEndpoint(trimmed), {
        method: 'GET',
        credentials: 'same-origin',
        redirect: 'manual',
      });
    } catch (error) {
      console.warn('[GumUpsellB] Failed to apply discount code', trimmed, error);
    }
  };

  class GumUpsellBCartBridge {
    constructor() {
      this.isFlushing = false;
      this.isAttached = false;
      this.retryPassForms = new WeakSet();
    }

    attach() {
      if (this.isAttached) return;
      document.addEventListener('submit', this.onSubmit, true);
      this.isAttached = true;
    }

    findUpsell() {
      return document.querySelector('gum-upsell-b');
    }

    onSubmit = (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (form.getAttribute('data-type') !== 'add-to-cart-form') return;

      const upsell = this.findUpsell();
      if (!(upsell instanceof GumUpsellB) || !upsell.hasCommittedSelection()) return;

      if (this.retryPassForms.has(form)) {
        this.retryPassForms.delete(form);
        return;
      }

      if (this.isFlushing) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }

      const items = upsell.buildCartItems();
      if (!items.length) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      const submitter = event.submitter;
      const discountCode = upsell.hasSubscriptionCommitted()
        ? upsell.getSubscriptionDiscountCode()
        : '';
      void this.flushGumsThenResume(form, items, submitter, discountCode);
    };

    async flushGumsThenResume(form, items, submitter, discountCode = '') {
      this.isFlushing = true;

      const submitButtons = Array.from(
        form.querySelectorAll('button[type="submit"], button.product-form__submit, button[name="add"]')
      );
      const setLoading = (isLoading) => {
        submitButtons.forEach((btn) => {
          const spinner = btn.querySelector('.loading-overlay__spinner');
          if (isLoading) {
            btn.classList.add('loading');
            btn.setAttribute('aria-busy', 'true');
            // Don't disable — requestSubmit(submitter) fails if submitter is disabled.
            btn.style.pointerEvents = 'none';
            if (spinner) spinner.classList.remove('hidden');
          } else {
            btn.classList.remove('loading');
            btn.removeAttribute('aria-busy');
            btn.style.pointerEvents = '';
            if (spinner) spinner.classList.add('hidden');
          }
        });
      };

      const clearPointerEventsLock = () => {
        submitButtons.forEach((btn) => {
          btn.style.pointerEvents = '';
        });
      };

      setLoading(true);

      let succeeded = false;
      try {
        const addUrl = getCartAddEndpoint();
        const response = await fetch(addUrl, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ items }),
        });

        if (!response.ok) {
          let message = `Could not add gum upsell (${response.status})`;
          try {
            const data = await response.json();
            if (data?.description) message = data.description;
            else if (data?.message) message = data.message;
          } catch (e) {
            /* ignore */
          }
          throw new Error(message);
        }

        if (discountCode) {
          await applyDiscountCode(discountCode);
        }

        this.retryPassForms.add(form);
        succeeded = true;

        // Tell clear-cart-before-atc.js's skip-cart+clear-cart handler (LP
        // "Buy Now" flow) not to clear the cart on this retry submit — we
        // just added the gum items above, and its own clear() would wipe
        // them before the main product gets added, leaving only the main
        // product at checkout.
        form.__nwSkipNextCartClear = true;

        // Unlock before resume / checkout redirect; keep spinner visible.
        clearPointerEventsLock();

        if (typeof form.requestSubmit === 'function') {
          form.requestSubmit(submitter instanceof HTMLElement ? submitter : undefined);
        } else {
          form.submit();
        }
      } catch (error) {
        console.error('[GumUpsellB] Failed to add gum items before toothpaste ATC', error);
        const errorWrap = form.closest('product-form')?.querySelector('.product-form__error-message-wrapper');
        const errorText = errorWrap?.querySelector('.product-form__error-message');
        if (errorWrap && errorText) {
          errorText.textContent = error?.message || 'Unable to add gum upsell. Please try again.';
          errorWrap.hidden = false;
        }
      } finally {
        this.isFlushing = false;
        // Keep loader on success through toothpaste ATC → checkout.
        if (!succeeded) setLoading(false);
      }
    }
  }

  if (!customElements.get('gum-upsell-b')) {
    customElements.define('gum-upsell-b', GumUpsellB);
  }

  if (!window.__gumUpsellBCartBridge) {
    window.__gumUpsellBCartBridge = new GumUpsellBCartBridge();
    window.__gumUpsellBCartBridge.attach();
  }
})();
